import { useEffect, useState } from "react";
import type { Project, Repository, TestSelection, Values } from "@proof/core";
import { FlaskConical } from "lucide-react";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Proof } from "./Proof";
import { Bulk } from "./Bulk";
import { Button } from "./components";
import {
  TestSelectionControls,
  initialTestSelection,
  selectionKey,
  useResolvedTestSelection,
  useTestSources,
} from "./TestSelectionControls";

type DemoProfileId = "alex" | "jamie" | "guest";

export interface LiveProofSelection {
  projectId: string;
  selection: TestSelection;
  versionId?: string;
}

export function PublishedWorkspaceView({
  sourceParameters,
  onSourceParametersChange,
  project,
  repository,
  mode,
  values,
  onValues,
  partId,
  onPart,
  notify,
  onPreviewSelection,
  onTestForm,
  onSaveDefault,
  demoProfile,
  onDemoProfile,
}: {
  sourceParameters?: import("@proof/core").SourceParameters;
  onSourceParametersChange?: (
    values: import("@proof/core").SourceParameters,
  ) => void;
  project: Project;
  repository: Repository;
  mode: "proof" | "bulk";
  values: Values;
  onValues: (values: Values) => void;
  partId: string;
  onPart: (id: string) => void;
  notify: (message: string) => void;
  onPreviewSelection?: (selection: LiveProofSelection | undefined) => void;
  /** Opens the selection in a separate test window (same flow as Impersonation). */
  onTestForm?: (selection: TestSelection) => void;
  /** Stores the project's default selection; shown to document managers only. */
  onSaveDefault?: (selection: TestSelection) => Promise<void>;
  demoProfile?: DemoProfileId;
  onDemoProfile?: (profile: DemoProfileId) => void;
}) {
  const transport = useWorkspaceTransport();
  const allowDrafts = transport.can("tenant.documents.manage");
  const { sources, error } = useTestSources(repository, project, allowDrafts);
  const [chosen, setChosen] = useState<TestSelection>();
  const selection =
    chosen ||
    initialTestSelection(project, sources, allowDrafts, transport.demo);
  const resolved = useResolvedTestSelection(
    selection,
    project,
    sources,
    allowDrafts,
  );
  // Demo keeps proofs local and unversioned, as before.
  const versionId = transport.demo ? undefined : resolved?.versionId;
  useEffect(() => {
    transport.setTestSelection(
      project.id,
      resolved && !resolved.versionId ? resolved.selection : undefined,
    );
    onPreviewSelection?.(
      resolved
        ? {
            projectId: project.id,
            selection: resolved.selection,
            versionId: resolved.versionId,
          }
        : undefined,
    );
  }, [transport, project.id, resolved, onPreviewSelection]);
  useEffect(
    () => () => {
      transport.setTestSelection(project.id, undefined);
      onPreviewSelection?.(undefined);
    },
    [transport, project.id, onPreviewSelection],
  );
  if (error) return <p role="alert">{error}</p>;
  if (!sources) return <p role="status">Loading document versions…</p>;
  const change = (next: TestSelection) => {
    setChosen(next);
    onValues({});
    onSourceParametersChange?.({});
    onPart("");
  };
  return (
    <>
      <section className="panel padded live-proof-selection">
        <TestSelectionControls
          idPrefix="live-proof"
          sources={sources}
          allowDrafts={allowDrafts}
          value={selection}
          onChange={change}
          current={project}
          resolved={resolved}
          storedDefault={project.settings.testSelection}
          onSaveDefault={allowDrafts ? onSaveDefault : undefined}
        />
        {onTestForm && resolved && (
          <div className="live-proof-actions">
            <Button onClick={() => onTestForm(resolved.selection)}>
              <FlaskConical size={16} />
              Test this form
            </Button>
          </div>
        )}
        {!sources.versions.length && (
          <p>
            No published versions.{" "}
            {transport.can("tenant.templates.publish")
              ? "Use Publish to create the first immutable version."
              : "Ask a workspace publisher to publish this document."}
          </p>
        )}
      </section>
      {!resolved ? (
        <p>No published version is available for proofing.</p>
      ) : mode === "proof" ? (
        <Proof
          sourceParameters={sourceParameters}
          onSourceParametersChange={onSourceParametersChange}
          key={selectionKey(resolved.selection)}
          project={resolved.project}
          repository={repository}
          values={values}
          onValues={onValues}
          partId={partId}
          onPart={onPart}
          versionId={versionId}
          demoProfile={demoProfile}
          onDemoProfile={onDemoProfile}
          notify={notify}
        />
      ) : resolved.versionId ? (
        <Bulk
          key={resolved.versionId}
          project={resolved.project}
          versionId={resolved.versionId}
          notify={notify}
        />
      ) : (
        <p role="status">
          Select a published version to prepare bulk generation.
        </p>
      )}
    </>
  );
}
